import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { differsAtCentPrecision } from '../validation-field-key.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isMandatoryForValidation } from '../../../utils/required-field.util';
import {
  accountingStandardToTaxonomy,
  buildRole9DualColumnKey,
  extractRole9SchemaTaxonomy,
  isRole9Empty,
  isRole9StaticallyRequired,
  pickRole9Schema,
  resolveRole9VisibleColumns,
  Role9EqualityRule,
  Role9Schema,
  Role9SchemaControl,
} from './role9-sub-classification.rules';
import {
  accountingStandardToXbrlPrefix,
  calculator,
  hasCalculatedFieldMismatch,
  isResolvedAccountingStandard,
} from './calc-diff.helper';

/**
 * Pure validator for Role 9 (Sub-classification of Assets, Liabilities &
 * Equity).
 *
 * Mirrors the editor's role9-210100-sub-classification.component.ts surface
 * that flows through `evaluateSheetStatus()` →
 * `updateSheetLevelStatus('subClassificationOfAssetsLiabilitiesEquity', ...)`:
 *
 *   1. Static required check (latent-bug guard) — if any control declares
 *      `required: true` OR `validators: [{ name: 'required' }]`, the
 *      validator flags a Failed row when the corresponding flat key is
 *      empty. Both schemas declare zero such controls today, so this
 *      branch is preserved purely for safety against future schema edits
 *      (matches the LB-009/010/011 guard pattern from Tier 2).
 *
 *   2. Cross-sheet equality (sum) — for any control with
 *      `validation.equality` whose every rule has `operation: 'sum'`, the
 *      validator computes the sum of the referenced (sheet, field) pairs
 *      column-by-column and flags an error when the local control's value
 *      diverges from the sum by more than the rule's tolerance (default
 *      cent precision, mirroring `EqualityValidationService.validateAllEqualityRules`).
 *      Only the MFRS schema declares an equality block today (on
 *      `ifrs-full:BiologicalAssets`, summed against role 8's
 *      Current+Noncurrent BiologicalAssets); MPERS is silently a no-op.
 *
 * Standard equality (operation !== 'sum') is intentionally NOT mirrored
 * here because the role 9 schemas declare none. If a future schema edit
 * adds one, the validator will simply skip those rules — flag this in the
 * latent-bugs ledger when the schema changes so we extend coverage in the
 * same PR rather than letting the parity harness drift.
 *
 * ---- Schema variant handling --------------------------------------------
 *
 * Role 9 ships TWO form JSONs (MFRS / MPERS). The component picks at mount
 * time from the filingPrerequisites accounting-standard field; the pure
 * path gets the same value through context.accountingStandard (assembled by
 * ConversionValidatorService.buildContext from filingInfo). Selection is
 * delegated to pickRole9Schema so component and validator stay DRY.
 *
 * EqualityValidationService.validateAllEqualityRules short-circuits when the
 * schema's `taxonomy` field disagrees with the orchestrator's currentTaxonomy.
 * Since pickRole9Schema returns the schema matching context.accountingStandard,
 * the two will agree by construction — but we keep the mismatch guard below
 * for parity safety in case the orchestrator's accountingStandard ever
 * arrives empty/unrecognised.
 *
 * ---- Cross-sheet bundle expectations ------------------------------------
 *
 * Equality rules read role 8 via
 * `context.crossSheetData?.get('statementOfFinancialPosition')`. The
 * orchestrator's SheetDependencyService already lists role 9 as dependent
 * on role 8 (sub-classification → SOFP), so the bundle is in scope on the
 * cache-miss path. When the bundle is absent (parity-side mid-init, or a
 * conversion the user has not opened) we treat role 8's values as null and
 * the rule's null-handling rules apply (sum=0 path silently no-ops; matches
 * the editor's pre-subscription state).
 *
 * Visible-column resolution now goes through the shared, config-driven
 * resolver (`resolveRole9VisibleColumns` → `resolveDimensionVisibility` +
 * the central registry) — the SAME resolver the mounted editor calls via
 * `CrossReferenceService.getVisibility`, so the two paths cannot drift on
 * which columns are shown/validated. It reads both `filingPrerequisites`
 * (Phase 1) and `filingInfo` (Phase 2), prereq-first. When both are absent /
 * the gate fields are unset it defaults to the broad set (Consolidated &
 * PreviousYear shown), mirroring the editor's pre-load state.
 */

const SUM_OPERATION = 'sum';

/** Read a sheet's payload from the cross-sheet bundle, tolerating absence. */
function readSheetPayload(
  context: SheetValidationContext,
  sheet: string
): Record<string, any> | undefined {
  const bundle = context.crossSheetData;
  if (!bundle) return undefined;
  return bundle.get(sheet as Parameters<typeof bundle.get>[0]) as
    | Record<string, any>
    | undefined;
}

/**
 * Coerce a raw cell value into a number for equality comparison. Mirrors
 * validation-field-value.util's getFieldValueForColumn coercion: null/undefined/
 * empty-string return null; non-numeric strings return null; numeric strings
 * are parsed.
 */
function coerceCellValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mirrors the editor's `getFieldValueForColumn` calculated/readonly
 * fallback: a null cell on a calculated/readonly field reads as 0 instead
 * of "skip the column". This matters because role 9's `BiologicalAssets`
 * is calculated — without the fallback, an all-null role 9 row would
 * silently bypass its equality check.
 *
 * The fallback is keyed on the SCHEMA's view of the field (role 9 schema
 * for the local control; orchestrator does not bundle role 8's schema, so
 * for cross-sheet sources we use `null` instead of `0` to match the
 * editor's behaviour when role 8's schema isn't cached).
 */
function readLocalCellValue(
  payload: Record<string, any> | undefined,
  key: string,
  control: Role9SchemaControl
): number | null {
  const raw = payload?.[key];
  const coerced = coerceCellValue(raw);
  if (coerced !== null) return coerced;
  if (!payload || Object.keys(payload).length === 0) {
    if (control.ui?.calculated || control.ui?.readonly) return 0;
  }
  if (control.ui?.calculated || control.ui?.readonly) return 0;
  return null;
}

function readCrossSheetCellValue(
  payload: Record<string, any> | undefined,
  key: string
): number | null {
  return coerceCellValue(payload?.[key]);
}

function isSumValidation(rules: ReadonlyArray<Role9EqualityRule>): boolean {
  return rules.length > 0 && rules.every((rule) => rule.operation === SUM_OPERATION);
}

/**
 * Latent-bug guard (LB-001 pattern). Both role 9 schemas have ZERO
 * required fields today; this loop is structurally a no-op against the
 * current schema but mirrors the editor's `getRowValidationStatus()`
 * "required field with no value → Failed" branch for any future schema
 * edit. The component's same-source check would also need expanding when
 * that day comes — see role 6 LB-001 for the precedent.
 */
function checkStaticRequiredErrors(
  schema: Role9Schema,
  sheetData: Record<string, any>
): boolean {
  for (const control of schema.controls) {
    if (control.type === 'SectionHeader') continue;
    // `isMandatoryForValidation` excludes computed controls, so the 24 MFRS /
    // 21 MPERS calculated totals the PO's list flags on this sheet light their
    // `*` without becoming submit blocks. See the Developer Notes on that
    // function. `isRole9StaticallyRequired` still gates the LB-001 detection.
    if (!isMandatoryForValidation(control)) continue;
    if (!isRole9StaticallyRequired(control)) continue;
    if (isRole9Empty(sheetData[control.qname])) {
      return true;
    }
  }
  return false;
}

/**
 * Walk every control with a sum-style equality block, collect all failures
 * into `errorsOut` (first failure per qname wins when the same field spans
 * multiple columns), and return true if any (control, column) pair failed.
 * The caller always passes a sink — this function never short-circuits on
 * first failure; it continues scanning so the diagnostic log receives the
 * full set of disagreements.
 */
function checkCrossSheetSumEqualityErrors(
  schema: Role9Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext,
  taxonomy: 'MFRS' | 'MPERS',
  errorsOut: { [field: string]: string }
): boolean {
  // The shared resolver is prereq-first with a filingInfo fallback (Phase 1
  // writes filingPrerequisites, Phase 2 writes filingInfo), so pass BOTH
  // slices — the same posture the mounted editor's
  // CrossReferenceService.getVisibility uses.
  const visibleColumns = resolveRole9VisibleColumns({
    filingPrerequisites: readSheetPayload(context, SHEET_KEYS.filingPrerequisites),
    filingInfo: readSheetPayload(context, SHEET_KEYS.filingInfo),
  });
  let anyError = false;

  for (const control of schema.controls) {
    const rules = control.validation?.equality;
    if (!rules || rules.length === 0) continue;
    if (!isSumValidation(rules)) continue;

    for (const { dimension, period } of visibleColumns) {
      const localKey = buildRole9DualColumnKey(control.qname, dimension, period);
      const currentValue = readLocalCellValue(sheetData, localKey, control);

      // Editor's sum branch: if the local value is null, the column is
      // skipped (no error possible because there is nothing to compare
      // against). Matches `if (currentValue === null) continue` in
      // EqualityValidationService's sum branch.
      if (currentValue === null) continue;

      let sumValue = 0;
      let anyContribution = false;

      for (const rule of rules) {
        if (
          rule.taxonomy &&
          rule.taxonomy.toUpperCase() !== taxonomy.toUpperCase()
        ) {
          continue;
        }

        const sourcePayload = readSheetPayload(context, rule.sheet);
        const sourceKey = buildRole9DualColumnKey(rule.field, dimension, period);
        const sourceValue = readCrossSheetCellValue(sourcePayload, sourceKey);

        if (sourceValue === null) continue;

        sumValue += sourceValue;
        anyContribution = true;
      }

      // If no source contributed AND the bundle was absent, treat the
      // column as not-comparable (matches editor: sumValue stays 0 and a
      // 0-vs-currentValue comparison would still fire — but only when the
      // bundle was present and merely had nulls, which is also the
      // editor's behaviour). The pure path therefore intentionally still
      // compares against sumValue=0 when ANY rule resolved its source
      // sheet, even if every individual cell was null.
      if (
        !anyContribution &&
        !context.crossSheetData?.has(
          rules[0].sheet as Parameters<NonNullable<SheetValidationContext['crossSheetData']>['has']>[0]
        )
      ) {
        continue;
      }

      // Round to cents, flag any difference (≥ 0.01): a total that is one cent
      // off its component sum must be reported (old `> tolerance` needed ≥ 0.02).
      if (differsAtCentPrecision(currentValue, sumValue)) {
        anyError = true;
        // Record first failure per qname; continue collecting remaining columns.
        if (errorsOut[control.qname] === undefined) {
          const fmt = (value: number) => Number(value.toFixed(6));
          errorsOut[control.qname] =
            `${control.qname} [${dimension}/${period}]: expected Σ = ${fmt(sumValue)} ` +
            `(${rules.map((rule) => rule.field).join(' + ')}), got ${fmt(currentValue)}, ` +
            `diff ${fmt(Math.abs(currentValue - sumValue))}`;
        }
      }
    }
  }

  return anyError;
}

export const role9SubClassificationValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity,
  // Tier 3: cross-sheet reads (role 8 SOFP) without true row-array
  // repetition or local calculations driving the parity result. The
  // editor's calculated totals (CalculationService.calculateTotals_210100)
  // populate input cells but the pure validator does NOT need to replay
  // them — it reads the persisted post-calculation values from sheetData
  // directly, mirroring how getRowValidationStatus() inspects the form.
  tier: 3,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const schema = pickRole9Schema(context.accountingStandard);
    const contextTaxonomy = accountingStandardToTaxonomy(
      context.accountingStandard
    );
    const schemaTaxonomy = extractRole9SchemaTaxonomy(schema);

    // Match EqualityValidationService's strict-mode guard: when the schema's
    // taxonomy disagrees with the orchestrator's notion of taxonomy, every
    // equality check is skipped. By construction pickRole9Schema returns a
    // schema whose taxonomy matches contextTaxonomy, so this branch only
    // fires for unrecognised accountingStandard values — same as the editor.
    const equalityActive =
      schemaTaxonomy !== null && schemaTaxonomy === contextTaxonomy;

    const hasStaticRequiredError = checkStaticRequiredErrors(schema, sheetData);
    const equalityErrors: { [field: string]: string } = {};
    const hasEqualityError = equalityActive
      ? checkCrossSheetSumEqualityErrors(
          schema,
          sheetData,
          context,
          contextTaxonomy,
          equalityErrors
        )
      : false;

    const hasErrors = hasStaticRequiredError || hasEqualityError;
    // ------------------
    // Diagnostic logs retired — parity harness now warns (non-blocking) with a populated errors map. See Spec A.
    // console.log('[On Role 9 Sub-classification of Assets, Liabilities & Equity | Validation]: Data: ', sheetData);
    // console.log('[On Role 9 Sub-classification of Assets, Liabilities & Equity | Validation]: Taxonomies Agree: ', equalityActive, '(context:', contextTaxonomy, ', schema:', schemaTaxonomy, ')');
    // console.log('[On Role 9 Sub-classification of Assets, Liabilities & Equity | Validation]: Has Static Required-Field Errors: ', hasStaticRequiredError);
    // console.log('[On Role 9 Sub-classification of Assets, Liabilities & Equity | Validation]: Has Equality Errors: ', hasEqualityError);
    // ------------------
    if (hasErrors) {
      return {
        hasErrors: true,
        hasSuggestedChanges: false,
        errors: Object.keys(equalityErrors).length > 0 ? equalityErrors : undefined,
      };
    }

    // AS-842 calc-diff: surface the editor's `Validated*` signal in the
    // silent path by recomputing role 9's calculated totals and diffing
    // against the persisted (N8N-extracted) values. Any mismatch flips
    // `hasSuggestedChanges`, which the resolver rolls up into a `Success*`
    // chip on the listing.
    const recomputed = calculator.calculateTotals_210100(
      sheetData,
      accountingStandardToXbrlPrefix(context.accountingStandard)
    );
    return {
      hasErrors: false,
      // Guard: only diff when the taxonomy is genuinely known. An unresolved
      // accountingStandard ('') would default the recompute to the MFRS prefix
      // and manufacture a false `Success *` on MPERS data (e.g. ssmt:CashAndBankBalances
      // mis-summed to 0 against an extracted 47.85). See isResolvedAccountingStandard.
      hasSuggestedChanges:
        isResolvedAccountingStandard(context.accountingStandard) &&
        hasCalculatedFieldMismatch(context.extractedBaseline ?? sheetData, recomputed),
    };
  },
};
